import AdminButton from 'components/button';
import Button from 'components/crm/ui/Button';
import DateField from 'components/crm/ui/DateField';
import Label from 'components/crm/ui/Label';
import Switch from 'components/crm/ui/Switch';
import TextField from 'components/crm/ui/TextField';
import AttendeesField from 'components/planning/fields/AttendeesField';
import ColorField from 'components/planning/fields/ColorField';
import RecurrenceField from 'components/planning/fields/RecurrenceField';
import RemindersField from 'components/planning/fields/RemindersField';
import ScopeDialog from 'components/planning/ScopeDialog';
import WeekPicker from 'components/planning/WeekPicker';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import useWideSlideOver from 'components/SlideOver/wideSlideOver';
import { EVENT_CATEGORIES, eventCategoryText } from 'constants/eventCategories';
import { crmTaskErrorText } from 'helper/crmTaskErrors';
import { sameRule } from 'helper/recurrence';
import { addDays, fromIsoDay, isoDay } from 'helper/timeLabels';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

const pad = (value) => String(value).padStart(2, '0');
export const clockOf = (date) =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}`;

// The local moment of `day` (YYYY-MM-DD) at `clock` (HH:MM), or null.
export const momentOf = (day, clock) => {
  const [year, month, date] = (day ?? '').split('-').map(Number);
  const match = /^(\d{1,2}):(\d{2})$/.exec(clock ?? '');
  if (!year || !month || !date || !match) return null;
  return new Date(year, month - 1, date, Number(match[1]), Number(match[2]));
};

// The local day `days` after `day` (both YYYY-MM-DD).
export const shiftDay = (day, days) => isoDay(addDays(fromIsoDay(day), days));

const daysBetween = (from, to) =>
  from && to ? Math.round((fromIsoDay(to) - fromIsoDay(from)) / 86400000) : 0;

const MODES = ['', 'on_site', 'video', 'phone'];
const TRANSPARENCIES = ['opaque', 'transparent'];
const VISIBILITIES = ['default', 'public', 'private'];
const DEFAULT_REMINDERS = { useDefault: true, overrides: [] };

const fullName = (person) =>
  [person?.firstname, person?.lastname].filter(Boolean).join(' ');

// The slot of an appointment: its days are shown with the last one included,
// the api's endDay of a whole day being the day after.
const slotOf = (item) => {
  const start = new Date(item.startsAt);
  const end = new Date(item.endsAt);
  if (item.allDay) {
    const startDay = item.startDay ?? isoDay(start);
    return {
      allDay: true,
      startDay,
      endDay: item.endDay ? shiftDay(item.endDay, -1) : startDay,
      start: '09:00',
      end: '10:00',
    };
  }
  return {
    allDay: false,
    startDay: isoDay(start),
    endDay: isoDay(end),
    start: clockOf(start),
    end: clockOf(end),
  };
};

// The form of an appointment to edit; `initial` of a new one is already in
// the form's shape (the draft of the quick creation). A new one of a record
// is a client appointment with its contact; one of the planning, internal.
const formOf = (mode, item, initial, record) =>
  mode === 'create'
    ? {
        category: initial?.category ?? (record ? 'rdv_client' : 'interne'),
        contactName: initial?.contactName ?? record?.contactName ?? record?.contacts?.[0]?.name ?? '',
        title: initial?.title ?? '',
        ownerId: initial?.ownerId ?? null,
        allDay: Boolean(initial?.allDay),
        startDay: initial?.startDay ?? '',
        endDay: initial?.endDay ?? initial?.startDay ?? '',
        start: initial?.start ?? '09:00',
        end: initial?.end ?? '10:00',
        recurrence: initial?.recurrence ?? null,
        mode: '',
        attendees: [],
        meet: false,
        location: '',
        reminders: DEFAULT_REMINDERS,
        colorId: '',
        transparency: 'opaque',
        visibility: 'default',
        description: initial?.description ?? '',
      }
    : {
        title: item.title ?? '',
        ownerId: item.ownerId ?? null,
        ...slotOf(item),
        mode: item.mode ?? '',
        attendees: item.attendees ?? [],
        // An appointment with a Meet link already: the box stays ticked.
        meet: Boolean(item.meet) || item.conference?.kind === 'meet',
        location: item.location ?? '',
        reminders: item.reminders ?? DEFAULT_REMINDERS,
        colorId: item.colorId ?? '',
        transparency: item.transparency ?? 'opaque',
        visibility: item.visibility ?? 'default',
        description: item.description ?? '',
        recurrence: item.series?.rule ?? null,
      };

// A guest as the api takes it: a colleague by id, anyone else by address.
const toAttendeeInput = (attendee) =>
  attendee.userId
    ? { userId: attendee.userId }
    : {
        email: attendee.email,
        displayName: attendee.displayName || '',
        ...(attendee.contactId ? { contactId: attendee.contactId } : {}),
      };

const selectClass =
  'rounded-lg border border-lineStrong bg-white p-3 text-sm font-medium text-ink outline-none focus:border-primary';

const SelectField = ({ id, label, value, onChange, options }) => (
  <div className="flex flex-col gap-2">
    <Label htmlFor={id}>{label}</Label>
    <select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={selectClass}
    >
      {options.map(([optionValue, optionLabel]) => (
        <option key={optionValue} value={optionValue}>
          {optionLabel}
        </option>
      ))}
    </select>
  </div>
);

/**
 * The whole form of an appointment, in the order of Google Calendar: a new
 * one (`mode` 'create', from « Plus d'options » of the quick creation,
 * `initial` its draft) or « Modifier » of one (`mode` 'edit', `item` its
 * detail). The Meet link and the reminders only exist in a calendar synced
 * with Google (`calendarSynced`). Saves through createAppointment or
 * updateAppointment and hands the appointment returned to `onSaved`;
 * « Annuler » (and Échap while editing) calls `onCancel`. A title is required
 * only without a company, as on the api. A new one may repeat (« Répéter »,
 * `initial.recurrence` from the quick creation): its rule goes as
 * `recurrence`, none for « Ne se répète pas ». An occurrence of a series
 * (`item.recurring`) shows the rule of its series, editable unless Google
 * wrote it outside of our choices or it was not read yet; saving asks for its
 * scope first (ScopeDialog) and sends it with the fields, the rule only when
 * it changed.
 *
 * A new one may be of a record (`record`: { companyId, name, contacts,
 * contactName }): it is linked to its company and chooses its type and the
 * contact met. `layout` 'panel' fills a slide-over as in HubSpot: the week it
 * falls in on the left (WeekPicker, the slide-over widened), `header`, the
 * fields under `prepend` and the buttons in the panel's footer on the right.
 */
const AppointmentForm = ({
  mode = 'edit',
  item,
  initial,
  record = null,
  calendarSynced = false,
  layout = 'inline',
  header = null,
  prepend = null,
  onSaved,
  onCancel,
}) => {
  const creating = mode === 'create';
  const panel = layout === 'panel';
  useWideSlideOver(panel);
  const { text, language } = useText('appointment');
  const { text: planningText } = useText('planning');
  const { text: tasksText } = useText('tasks');
  const { text: fieldsText } = useText('appointmentFields');
  const { text: recurrenceText } = useText('recurrence');
  const { call, me } = useCrmContext();
  const [form, setForm] = useState(() => formOf(mode, item, initial, record));
  // Once the box is touched, a change of mode no longer ticks it.
  const [meetTouched, setMeetTouched] = useState(false);
  const [team, setTeam] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [scopeOpen, setScopeOpen] = useState(false);
  // In a panel, the week read beside the form tells whether Google is synced.
  const [weekSynced, setWeekSynced] = useState(false);
  const synced = calendarSynced || weekSynced;
  useEffect(() => {
    call(crmApi.getTeam).then(setTeam, () => setTeam([]));
    call(crmApi.getContacts).then(setContacts, () => setContacts([]));
  }, [call]);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  // Bubbling, after the calendar of the day field: an Échap that closes the
  // calendar (default prevented) leaves the form open. A new appointment sits
  // in a Drawer, which handles Échap itself.
  useEffect(() => {
    if (creating) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !event.defaultPrevented)
        cancelRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [creating]);

  const update = (patch) => setForm((current) => ({ ...current, ...patch }));
  const set = (field) => (event) => update({ [field]: event.target.value });
  // The span is kept when the first day moves, as in Google Calendar.
  const setStartDay = (startDay) =>
    setForm((current) => ({
      ...current,
      startDay,
      endDay: shiftDay(startDay, daysBetween(current.startDay, current.endDay)),
    }));
  const setMode = (nextMode) =>
    setForm((current) => ({
      ...current,
      mode: nextMode,
      ...(nextMode === 'video' && !meetTouched && synced
        ? { meet: true }
        : {}),
    }));

  const colleagues = team.filter((person) => person.id !== me?.id);
  const ownerId = creating
    ? form.ownerId && form.ownerId !== me?.id
      ? form.ownerId
      : null
    : item.ownerId;
  const owner = colleagues.find((person) => person.id === ownerId) ?? null;
  const calendarOwnerId = ownerId ?? me?.id;
  const guests = team.filter((person) => person.id !== calendarOwnerId);

  const startsAt = momentOf(form.startDay, form.start);
  const endsAt = momentOf(form.endDay, form.end);
  const timeKnown = form.allDay
    ? Boolean(form.startDay && form.endDay)
    : Boolean(startsAt && endsAt);
  const timeOk = form.allDay
    ? timeKnown && form.endDay >= form.startDay
    : timeKnown && endsAt > startsAt;
  const timeError =
    timeKnown && !timeOk ? tasksText('errors.INVALID_APPOINTMENT_TIME') : null;
  const titleOk =
    Boolean(form.title.trim()) ||
    (creating ? Boolean(record?.companyId) : Boolean(item.companyId || item.company));
  const canSave = timeOk && titleOk && !busy;
  const recurring = !creating && Boolean(item.recurring);
  const ruleEditable = recurring && Boolean(item.series?.rule);
  const ruleChanged =
    ruleEditable && !sameRule(form.recurrence, item.series.rule);
  // Fixed while editing: a rule chosen among the presets must not follow a
  // move of this one occurrence to another day.
  const [ruleDay] = useState(() => (creating ? null : slotOf(item).startDay));

  const save = async (scope) => {
    if (!canSave) return;
    if (recurring && !scope) {
      setScopeOpen(true);
      return;
    }
    setScopeOpen(false);
    setBusy(true);
    setError('');
    const slot = form.allDay
      ? {
          allDay: true,
          startDay: form.startDay,
          endDay: shiftDay(form.endDay, 1),
        }
      : {
          allDay: false,
          startsAt: startsAt.toISOString(),
          endsAt: endsAt.toISOString(),
        };
    const fields = {
      title: form.title.trim(),
      location: form.location.trim(),
      ...slot,
      description: form.description.trim(),
      mode: form.mode,
      attendees: form.attendees.map(toAttendeeInput),
      meet: form.meet,
      reminders: form.reminders,
      colorId: form.colorId,
      transparency: form.transparency,
      visibility: form.visibility,
    };
    try {
      if (creating) {
        const created = await call(crmApi.createAppointment, {
          ...(ownerId ? { ownerId } : {}),
          ...fields,
          ...(form.recurrence ? { recurrence: form.recurrence } : {}),
          category: form.category,
          ...(record?.companyId ? { companyId: record.companyId } : {}),
          ...(form.contactName ? { contactName: form.contactName } : {}),
        });
        toast.success(
          owner
            ? planningText('meetingAddedFor', { name: fullName(owner) })
            : planningText('meetingAdded'),
        );
        onSaved(created);
      } else {
        const updated = await call(crmApi.updateAppointment, {
          id: item.id,
          fields: ruleChanged
            ? { ...fields, recurrence: form.recurrence }
            : fields,
          ...(scope ? { scope } : {}),
        });
        toast.success(text('updated'));
        onSaved(updated);
      }
    } catch (failure) {
      const message = crmTaskErrorText(failure, tasksText);
      if (message) setError(message);
      setBusy(false);
    }
  };

  // A slot picked in the week: its day and start, its length kept on a click.
  const pickSlot = ({ start, minutes }) =>
    setForm((current) => {
      const from = momentOf(current.startDay, current.start);
      const to = momentOf(current.endDay, current.end);
      const length = minutes ?? (from && to && to > from ? (to - from) / 60000 : 60);
      return {
        ...current,
        allDay: false,
        startDay: isoDay(start),
        endDay: isoDay(new Date(start.getTime() + length * 60000)),
        start: clockOf(start),
        end: clockOf(new Date(start.getTime() + length * 60000)),
      };
    });

  const fields = (
    <div className="flex flex-col gap-4">
      {prepend}
      {creating && (
        <SelectField
          id="appointment-form-category"
          label={fieldsText('categoryLabel')}
          value={form.category}
          onChange={(category) => update({ category })}
          options={Object.keys(EVENT_CATEGORIES).map((category) => [
            category,
            eventCategoryText(language, category, 'label'),
          ])}
        />
      )}
      {creating && record?.contacts?.length > 0 && (
        <SelectField
          id="appointment-form-contact"
          label={fieldsText('contactLabel')}
          value={form.contactName}
          onChange={(contactName) => update({ contactName })}
          options={record.contacts.map((contact) => [contact.name, contact.name])}
        />
      )}
      <TextField
        id="appointment-form-title"
        label={planningText('titleLabel')}
        value={form.title}
        maxLength={200}
        onChange={set('title')}
      />
      {creating && (colleagues.length > 0 || ownerId) && (
        <SelectField
          id="appointment-form-owner"
          label={planningText('calendarOf')}
          value={ownerId ?? ''}
          onChange={(value) =>
            setForm((current) => ({
              ...current,
              ownerId: value || null,
              // The new owner cannot stay a guest of their own appointment.
              attendees: current.attendees.filter(
                (attendee) => attendee.userId !== (value || me?.id),
              ),
            }))
          }
          options={[
            ['', fullName(me)],
            ...(ownerId && !owner ? [[ownerId, '']] : []),
            ...colleagues.map((person) => [person.id, fullName(person)]),
          ]}
        />
      )}
      <Switch
        checked={form.allDay}
        onChange={(allDay) => update({ allDay })}
        label={text('allDayLabel')}
      />
      <div className="grid grid-cols-2 gap-3">
        <DateField
          id="appointment-form-start-day"
          label={text('startDayLabel')}
          value={form.startDay}
          onChange={setStartDay}
        />
        {!form.allDay && (
          <TextField
            id="appointment-form-start"
            type="time"
            label={text('startLabel')}
            value={form.start}
            onChange={set('start')}
          />
        )}
        <div className="flex flex-col gap-2">
          <DateField
            id="appointment-form-end-day"
            label={text('endDayLabel')}
            value={form.endDay}
            minDate={fromIsoDay(form.startDay || isoDay(new Date()))}
            onChange={(endDay) => update({ endDay })}
          />
          {form.allDay && timeError && (
            <p className="text-sm text-danger">{timeError}</p>
          )}
        </div>
        {!form.allDay && (
          <TextField
            id="appointment-form-end"
            type="time"
            label={text('endLabel')}
            value={form.end}
            onChange={set('end')}
            error={timeError}
          />
        )}
      </div>
      {creating && (
        <RecurrenceField
          value={form.recurrence}
          startDay={form.startDay}
          onChange={(recurrence) => update({ recurrence })}
        />
      )}
      {ruleEditable && (
        <RecurrenceField
          value={form.recurrence}
          startDay={ruleDay}
          onChange={(recurrence) => update({ recurrence })}
        />
      )}
      {recurring && !ruleEditable && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium leading-5 text-body">
            {recurrenceText('label')}
          </span>
          <p className="text-sm font-medium text-ink">
            {recurrenceText(item.series ? 'googleCustom' : 'repeats')}
          </p>
        </div>
      )}
      <SelectField
        id="appointment-form-mode"
        label={fieldsText('modeLabel')}
        value={form.mode}
        onChange={setMode}
        options={MODES.map((value) => [
          value,
          fieldsText(`mode_${value || 'none'}`),
        ])}
      />
      <AttendeesField
        value={form.attendees}
        onChange={(attendees) => update({ attendees })}
        team={guests}
        contacts={contacts}
        ownerEmail={creating ? '' : (item.owner?.email ?? '')}
        ownerId={calendarOwnerId}
      />
      {synced && (
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium leading-5 text-ink">
          <input
            type="checkbox"
            className="checkbox-primary"
            checked={form.meet}
            onChange={(event) => {
              setMeetTouched(true);
              update({ meet: event.target.checked });
            }}
          />
          {fieldsText('meetLabel')}
        </label>
      )}
      <TextField
        id="appointment-form-location"
        label={text('locationLabel')}
        value={form.location}
        maxLength={200}
        onChange={set('location')}
      />
      {synced && (
        <RemindersField
          value={form.reminders}
          onChange={(reminders) => update({ reminders })}
        />
      )}
      <ColorField
        value={form.colorId}
        onChange={(colorId) => update({ colorId })}
      />
      <SelectField
        id="appointment-form-transparency"
        label={fieldsText('transparencyLabel')}
        value={form.transparency}
        onChange={(transparency) => update({ transparency })}
        options={TRANSPARENCIES.map((value) => [
          value,
          fieldsText(`transparency_${value}`),
        ])}
      />
      <SelectField
        id="appointment-form-visibility"
        label={fieldsText('visibilityLabel')}
        value={form.visibility}
        onChange={(visibility) => update({ visibility })}
        options={VISIBILITIES.map((value) => [
          value,
          fieldsText(`visibility_${value}`),
        ])}
      />
      <TextField
        id="appointment-form-description"
        multiline
        rows={4}
        label={planningText('descriptionLabel')}
        value={form.description}
        maxLength={5000}
        onChange={set('description')}
      />
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
  const scopeDialog = (
    <ScopeDialog
      open={scopeOpen}
      action="edit"
      ruleChanged={ruleChanged}
      onCancel={() => setScopeOpen(false)}
      onConfirm={save}
    />
  );

  if (!panel) {
    return (
      <div className="flex flex-col gap-4">
        {fields}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {text('cancelEdit')}
          </Button>
          <Button disabled={!canSave} onClick={() => save()}>
            {text('saveEdit')}
          </Button>
        </div>
        {scopeDialog}
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1">
      <div className="hidden min-w-0 flex-1 flex-col border-r border-gray-100 lg:flex">
        <WeekPicker
          start={!form.allDay ? startsAt : null}
          minutes={startsAt && endsAt && endsAt > startsAt ? (endsAt - startsAt) / 60000 : 60}
          title={form.title.trim() || record?.name || ''}
          onPick={pickSlot}
          onLoaded={(week) => setWeekSynced(Boolean(week?.calendarSynced))}
        />
      </div>
      <div className="flex min-h-0 w-full flex-col lg:w-[480px] lg:flex-none">
        {header}
        <SlideOverBody>{fields}</SlideOverBody>
        <div className="flex-shrink-0 border-t border-gray-100">
          <SlideOverFooter>
            <AdminButton color="SECONDARY" size="SM" onClick={onCancel}>
              {text('cancelEdit')}
            </AdminButton>
            <AdminButton color={canSave ? 'PRIMARY' : 'DISABLED'} size="SM" disabled={!canSave} onClick={() => save()}>
              {creating ? fieldsText('createCta') : text('saveEdit')}
            </AdminButton>
          </SlideOverFooter>
        </div>
      </div>
      {scopeDialog}
    </div>
  );
};

export default AppointmentForm;
